import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { themeStore } from 'Models/ThemeStore';
import { getParkingLocations, ParkingLocationDto } from 'Services/Api/_HumanWritten/ParkingService';
import BookingPromoCard from './BookingPromoCard';

/**
 * Translation IDs for multilingual support in the BookingPromoCard.
 * These keys correspond to text elements within the promo card.
 */
const translations = {
	title: 'booking-success__promo-card__title',
	description: 'booking-success__promo-card__description',
	price: 'booking-success__promo-card__price',
	priceSuffix: 'booking-success__promo-card__price-suffix',
	linkText: 'booking-success__promo-card__link-text',
} as const;

function isParkingPromoUrl(url: string): boolean {
	try {
		const parsed = new URL(url, window.location.origin);
		return parsed.searchParams.get('parking') === 'true';
	} catch {
		return false;
	}
}

export default function BookingSuccessPromoCard() {
	const linkURL = appConfigStore.bookingSuccessPromoUrl;
	const [visible, setVisible] = useState(!isParkingPromoUrl(linkURL));

	useEffect(() => {
		if (!isParkingPromoUrl(linkURL)) return;

		if (!appConfigStore.parkingEnabled || (!store.isStaff && !appConfigStore.parkingCustomerCanCreate)) {
			setVisible(false);
			return;
		}

		getParkingLocations()
			.then(locations => {
				const available = locations.some(
					(l: ParkingLocationDto) => !l.disabled && l.hasCapacityRestrictions && l.hasActivePricing,
				);
				setVisible(available);
			})
			.catch(() => {
				setVisible(false);
			});
	}, [linkURL]);

	if (!visible) return null;

	return (
		<BookingPromoCard
			imageURL={themeStore.bookingSuccessPromoCardImageURL}
			title={<FormattedMessage id={translations.title} defaultMessage="title" />}
			description={<FormattedMessage id={translations.description} defaultMessage="description" />}
			price={<FormattedMessage id={translations.price} defaultMessage="price" />}
			priceSuffix={<FormattedMessage id={translations.priceSuffix} defaultMessage="priceSuffix" />}
			linkURL={linkURL}
			linkText={<FormattedMessage id={translations.linkText} defaultMessage="linkText" />}
		/>
	);
}
